import { useState } from "react"
import { useTranslation } from "react-i18next"

import Box from "@material-ui/core/Box"
import Link from "@material-ui/core/Link"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemAvatar from "@material-ui/core/ListItemAvatar"
import ListItemText from "@material-ui/core/ListItemText"
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction"
import Dialog from "@material-ui/core/Dialog"
import Typography from "@material-ui/core/Typography"

import Chip from "../Chip"
import UserFormDialog from "./UserFormDialog"
import { UserRoleChip, roleColor } from "components/widgets"
import { sortUsers } from "common/helpers/admin-page-func.helper"
import { DialogTitle } from "components/partials"
import { UserAvatar } from "components/partials"
import { Role } from "common/types"

const Users = props => {
  const { t } = useTranslation()
  const [itemEdit, setItemEdit] = useState(null)
  const [isListDialogOpen, setIsListDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const { company, max = 5 } = props
  const users = sortUsers(props.users)
  const usersCount = users.length
  const usersLast = max - 1

  const toggleListDialog = state => () => {
    setIsListDialogOpen(state)
  }

  const toggleEditDialog = state => () => {
    setIsEditDialogOpen(state)
  }

  const setItem = item => () => {
    setItemEdit(item)
    toggleEditDialog(item !== null)()
  }

  return (
    <>
      <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={5}>
        {users.slice(0, usersLast).map(user => (
          <Chip
            key={user.id}
            label={user.fullName}
            avatar={<UserAvatar user={user} />}
            onClick={setItem(user)}
          />
        ))}
        {usersCount >= max && (
          <Link component="button" onClick={toggleListDialog(true)}>
            {`+${usersCount - usersLast} ${t("general.more")}`}
          </Link>
        )}
      </Box>

      <UsersDialog
        open={isListDialogOpen}
        onClose={toggleListDialog(false)}
        company={company}
        users={users}
        onClickItem={setItem}
      />
      <UserFormDialog
        user={itemEdit || {}}
        DialogProps={{
          open: isEditDialogOpen,
          onClose: toggleEditDialog(false),
          TransitionProps: {
            onExited: setItem(null)
          }
        }}
      />
    </>
  )
}

const UsersDialog = props => {
  const { t } = useTranslation()
  const { users, company, onClickItem, ...rest } = props
  const totalUsers = users.length

  function onClose() {
    if (rest.onClose) {
      rest.onClose({}, "backdropClick")
    }
  }

  const allowedUsers = company.unlimitedUserLicenses
    ? "∞"
    : String(company.userLicenses)

  return (
    <Dialog {...rest} fullWidth maxWidth="xs">
      <DialogTitle onClose={onClose}>
        <Box display="flex" gridGap={8} alignItems="baseline">
          <Typography variant="h6">{company.name}</Typography>
          <Typography>
            {`${totalUsers}/${allowedUsers}`}
            &nbsp;
            {totalUsers > 1
              ? t("view.adminCompanies.users")
              : t("view.adminCompanies.user")}
          </Typography>
        </Box>
      </DialogTitle>
      <Box display="flex" gridGap={8} paddingX={2}>
        <UsersSummary users={users} />
      </Box>
      <List>
        {users.map(user => (
          <ListItem button key={user.id} onClick={onClickItem(user)}>
            <ListItemAvatar>
              <UserAvatar user={user} />
            </ListItemAvatar>
            <ListItemText primary={user.fullName} secondary={user.email} />

            <ListItemSecondaryAction>
              <UserRoleChip role={user.role} />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Dialog>
  )
}

export const UsersSummary = props => {
  const { t } = useTranslation()
  const groups = getUserGroups(props.users)

  if (!groups.length) {
    return <Typography>{t("view.adminCompanies.noUsers")}</Typography>
  }

  return (
    <>
      {groups.map((group, key) => (
        <UserRoleCount
          key={key}
          role={Role[group.role]}
          total={group.users.length}
        />
      ))}
    </>
  )
}

const UserRoleCount = props => {
  const { role, total } = props
  const roleLowercased = role.toLowerCase()

  return (
    <Typography variant="body2" style={{ color: roleColor[role].color }}>
      {`${total} ${total > 1 ? `${roleLowercased}s` : roleLowercased}`}
    </Typography>
  )
}

function getUserGroups(users) {
  const groups = users.reduce((carry, user) => {
    const role = Role[user.role]
    const matchedGroup = carry.find(group => group.role === role)

    if (!matchedGroup) {
      return [...carry, { role, users: [user] }]
    }

    matchedGroup.users = [...matchedGroup.users, user]
    return carry.map(group => {
      if (group.role === matchedGroup.role) return matchedGroup
      return group
    })
  }, [])

  return groups.sort((groupA, groupB) => groupB.role - groupA.role)
}

export default Users
